import { useEffect, useMemo } from 'react';
import { useError } from 'react-use';

import { useRestPostQuery } from 'utils/hooks/api/useRestPostQuery';
import { InsightQueryRequest, ProfileQuery } from '../../types';
import { DEFAULT_QUERY } from '../../constants';
import { retryOnPartialContent } from '../../utils';
import { useInsightsResponseHandler } from './useInsightsResponseHandler';

const PROFILE_INSIGHTS_API = '/lists/insights/demographic';

const defaultProfileQuery: ProfileQuery = {
  emailDomains: [],
  brandAffinityLevels: [],
  devices: [],
  locations: [],
};

export const useProfileInsightsQuery = ({
  searchQuery,
  emailListId,
}: InsightQueryRequest = DEFAULT_QUERY) => {
  const params = useMemo(
    () => ({
      searchQuery,
      emailListId,
    }),
    [searchQuery, emailListId],
  );
  const {
    handleResponse,
    data = defaultProfileQuery,
    isRetrying,
  } = useInsightsResponseHandler<ProfileQuery>();

  const { refetch, isLoading, ...results } = useRestPostQuery<ProfileQuery, InsightQueryRequest>(
    PROFILE_INSIGHTS_API,
    params,
    {
      enabled: false,
      retry: retryOnPartialContent,
      onError: useError(),
    },
    undefined,
    handleResponse,
  );

  useEffect(() => {
    if (params?.emailListId) {
      refetch();
    }
  }, [params, refetch]);

  return {
    ...results,
    data,
    isLoading: isLoading && !isRetrying,
    refetch,
  };
};
